import {
    Directive,
    Input,
    TemplateRef,
    ViewContainerRef,
    OnInit,
    OnDestroy,
    ElementRef,
    Renderer2,
    EmbeddedViewRef
} from '@angular/core';
import { IntegrationCapabilityService } from '../services/integration-capability.service';
import { IntegrationCapability } from '../models/integration-capabilities.model';
import { Integration } from '../models/integration.model';
import { Subject, takeUntil } from 'rxjs';

/**
 * Structural Directive: *appRequiresCapability
 *
 * Conditionally shows/hides elements based on integration capabilities.
 * Can also disable elements instead of hiding them with a tooltip explaining why.
 *
 * @example
 * <!-- Hide if capability missing -->
 * <button *appRequiresCapability="'sequence_management'; integration: selectedIntegration">
 *   Edit Sequence
 * </button>
 *
 * @example
 * <!-- Disable instead of hide, with tooltip -->
 * <button *appRequiresCapability="'sequence_management'; integration: selectedIntegration; mode: 'disable'">
 *   Edit Sequence
 * </button>
 *
 * @example
 * <!-- Require multiple capabilities (AND logic) -->
 * <button *appRequiresCapability="['sequence_management', 'real_time_sync']; integration: selectedIntegration">
 *   Sync Sequence
 * </button>
 *
 * @example
 * <!-- Require any of multiple capabilities (OR logic) -->
 * <button *appRequiresCapability="['sequence_management', 'sequence_read_only']; integration: selectedIntegration; logic: 'any'">
 *   View Sequences
 * </button>
 */
@Directive({
    selector: '[appRequiresCapability]',
    standalone: true
})
export class RequiresCapabilityDirective implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private hasView = false;
    private embeddedViewRef: EmbeddedViewRef<any> | null = null;

    // Required: capability or capabilities to check
    @Input() appRequiresCapability: IntegrationCapability | IntegrationCapability[] | string | string[] = '';

    // Required: integration to check against
    @Input() appRequiresCapabilityIntegration: Integration | string | null = null;

    // Optional: 'hide' (default) or 'disable'
    @Input() appRequiresCapabilityMode: 'hide' | 'disable' = 'hide';

    // Optional: 'all' (default - AND logic) or 'any' (OR logic)
    @Input() appRequiresCapabilityLogic: 'all' | 'any' = 'all';

    // Optional: custom tooltip message
    @Input() appRequiresCapabilityTooltip?: string;

    // Optional: show a placeholder message when hidden
    @Input() appRequiresCapabilityPlaceholder?: string;

    constructor(
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef,
        private capabilityService: IntegrationCapabilityService,
        private renderer: Renderer2
    ) { }

    ngOnInit() {
        this.updateView();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private updateView() {
        const hasCapability = this.checkCapability();

        if (this.appRequiresCapabilityMode === 'hide') {
            this.handleHideMode(hasCapability);
        } else {
            this.handleDisableMode(hasCapability);
        }
    }

    private handleHideMode(hasCapability: boolean) {
        if (hasCapability && !this.hasView) {
            // Show the element
            this.embeddedViewRef = this.viewContainer.createEmbeddedView(this.templateRef);
            this.hasView = true;
        } else if (!hasCapability && this.hasView) {
            // Hide the element
            this.viewContainer.clear();
            this.hasView = false;
            this.embeddedViewRef = null;

            // Optionally show placeholder
            if (this.appRequiresCapabilityPlaceholder) {
                this.showPlaceholder();
            }
        }
    }

    private handleDisableMode(hasCapability: boolean) {
        // Always show the element in disable mode
        if (!this.hasView) {
            this.embeddedViewRef = this.viewContainer.createEmbeddedView(this.templateRef);
            this.hasView = true;
        }

        if (this.embeddedViewRef) {
            const element = this.embeddedViewRef.rootNodes[0] as HTMLElement;

            if (!hasCapability) {
                // Disable the element
                this.renderer.setAttribute(element, 'disabled', 'true');
                this.renderer.addClass(element, 'capability-disabled');

                // Add tooltip
                const tooltipText = this.appRequiresCapabilityTooltip || this.getDefaultTooltip();
                this.renderer.setAttribute(element, 'title', tooltipText);
                this.renderer.setAttribute(element, 'pTooltip', tooltipText);

                // Add visual styling
                this.renderer.setStyle(element, 'opacity', '0.5');
                this.renderer.setStyle(element, 'cursor', 'not-allowed');
            } else {
                // Enable the element
                this.renderer.removeAttribute(element, 'disabled');
                this.renderer.removeClass(element, 'capability-disabled');
                this.renderer.removeAttribute(element, 'title');
                this.renderer.removeAttribute(element, 'pTooltip');
                this.renderer.removeStyle(element, 'opacity');
                this.renderer.removeStyle(element, 'cursor');
            }
        }
    }

    private showPlaceholder() {
        // Create a text node or element to show as placeholder
        const placeholderDiv = this.renderer.createElement('div');
        this.renderer.addClass(placeholderDiv, 'capability-placeholder');
        const text = this.renderer.createText(this.appRequiresCapabilityPlaceholder!);
        this.renderer.appendChild(placeholderDiv, text);
        this.renderer.setStyle(placeholderDiv, 'color', '#999');
        this.renderer.setStyle(placeholderDiv, 'font-style', 'italic');
        this.renderer.setStyle(placeholderDiv, 'padding', '8px');

        // Insert placeholder
        const viewRef = this.viewContainer.createEmbeddedView(
            this.templateRef,
            {},
            0
        );
        if (viewRef.rootNodes[0]) {
            const parent = this.renderer.parentNode(viewRef.rootNodes[0]);
            if (parent) {
                this.renderer.appendChild(parent, placeholderDiv);
            }
        }
    }

    private checkCapability(): boolean {
        if (!this.appRequiresCapabilityIntegration) {
            return false;
        }

        const capabilities = Array.isArray(this.appRequiresCapability)
            ? this.appRequiresCapability
            : [this.appRequiresCapability];

        // Convert string capabilities to enum
        const capabilityEnums = capabilities.map(cap =>
            typeof cap === 'string' ? cap as IntegrationCapability : cap
        );

        if (this.appRequiresCapabilityLogic === 'any') {
            // OR logic - has any of the capabilities
            return this.capabilityService.hasAnyCapability(
                this.appRequiresCapabilityIntegration,
                capabilityEnums
            );
        } else {
            // AND logic - has all capabilities
            return this.capabilityService.hasAllCapabilities(
                this.appRequiresCapabilityIntegration,
                capabilityEnums
            );
        }
    }

    private getDefaultTooltip(): string {
        if (!this.appRequiresCapabilityIntegration) {
            return 'No integration selected';
        }

        const capability = Array.isArray(this.appRequiresCapability)
            ? this.appRequiresCapability[0]
            : this.appRequiresCapability;

        return this.capabilityService.getFeatureAvailabilityMessage(
            this.appRequiresCapabilityIntegration,
            capability as IntegrationCapability
        );
    }
}

/**
 * Attribute Directive: appCapabilityBadge
 *
 * Automatically adds capability badges to elements
 *
 * @example
 * <div appCapabilityBadge [integration]="selectedIntegration" [capability]="'sequence_management'">
 * </div>
 */
@Directive({
    selector: '[appCapabilityBadge]',
    standalone: true
})
export class CapabilityBadgeDirective implements OnInit {
    @Input() appCapabilityBadge: Integration | string | null = null;
    @Input() capability: IntegrationCapability | string = IntegrationCapability.SEQUENCE_MANAGEMENT;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private capabilityService: IntegrationCapabilityService
    ) { }

    ngOnInit() {
        if (!this.appCapabilityBadge) {
            return;
        }

        const badge = this.capabilityService.getCapabilityBadge(
            this.appCapabilityBadge,
            this.capability as IntegrationCapability
        );

        if (badge) {
            // Add badge content
            const badgeEl = this.renderer.createElement('span');
            this.renderer.addClass(badgeEl, 'capability-badge');
            this.renderer.addClass(badgeEl, `badge-${badge.severity}`);

            const icon = this.renderer.createElement('i');
            this.renderer.addClass(icon, 'pi');
            this.renderer.addClass(icon, badge.icon);

            const text = this.renderer.createText(` ${badge.text}`);

            this.renderer.appendChild(badgeEl, icon);
            this.renderer.appendChild(badgeEl, text);
            this.renderer.appendChild(this.el.nativeElement, badgeEl);

            // Add styling
            this.renderer.setStyle(badgeEl, 'display', 'inline-block');
            this.renderer.setStyle(badgeEl, 'padding', '4px 8px');
            this.renderer.setStyle(badgeEl, 'border-radius', '4px');
            this.renderer.setStyle(badgeEl, 'font-size', '12px');
            this.renderer.setStyle(badgeEl, 'margin-left', '8px');

            const colorMap: Record<string, { bg: string; color: string }> = {
                success: { bg: '#d1fae5', color: '#065f46' },
                info: { bg: '#dbeafe', color: '#1e40af' },
                warning: { bg: '#fef3c7', color: '#92400e' },
                danger: { bg: '#fee2e2', color: '#991b1b' }
            };

            const colors = colorMap[badge.severity] || colorMap['info'];
            this.renderer.setStyle(badgeEl, 'background-color', colors.bg);
            this.renderer.setStyle(badgeEl, 'color', colors.color);
        }
    }
}
